import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Empty,
  Input,
  Pagination,
  Select,
  Spin,
  Tag,
  message,
} from "antd";
import { MagnifyingGlass, UploadSimple } from "@phosphor-icons/react";
import { format, subMonths, subYears } from "date-fns";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  fetchBriefBankEntries,
  fetchBriefBankEntry,
  fetchBriefBankFacets,
} from "../../service/briefBank";
import BriefBankCopyModal from "./BriefBankCopyModal";
import BriefBankPreviewModal from "./BriefBankPreviewModal";
import BriefBankUploadModal from "./BriefBankUploadModal";
import {
  DetailHeading,
  DetailInfo,
  DetailLayout,
  DetailSection,
  DetailValue,
  DrawerFooter,
  EmptyState,
  FilterFooter,
  FilterLabel,
  Filters,
  Header,
  InlinePreviewPanel,
  Meta,
  Page,
  PaginationRow,
  ResultCard,
  ResultList,
  ResultSummary,
  ResultTitle,
  ResultTop,
  SearchPanel,
  SearchRow,
  Snippet,
  Subtitle,
  TagRow,
  Title,
} from "./BriefBankStyled";

const { RangePicker } = DatePicker;
const TODAY = () => format(new Date(), "yyyy-MM-dd");
const PRACTICE_LABELS = {
  va_cavc: "VA CAVC",
};

const responseData = (response) => response?.data || response || {};
const formatPracticeArea = (value) =>
  PRACTICE_LABELS[value] ||
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(" ");

const apiError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

const highlightedSnippet = (snippet) => {
  const parts = String(snippet || "").split(/(\[\[HIT\]\]|\[\[\/HIT\]\])/);
  let highlighted = false;
  return parts.map((part, index) => {
    if (part === "[[HIT]]") {
      highlighted = true;
      return null;
    }
    if (part === "[[/HIT]]") {
      highlighted = false;
      return null;
    }
    return highlighted ? <mark key={`${part}-${index}`}>{part}</mark> : part;
  });
};

const selectedValues = (params, key) => params.getAll(key).filter(Boolean);

const setMany = (params, key, values) => {
  params.delete(key);
  values.filter(Boolean).forEach((value) => params.append(key, value));
};

const dateDescription = (entry) => {
  if (!entry.filed_on) return "Filing date unknown";
  if (entry.filing_date_confidence === "high") return `Filed ${entry.filed_on}`;
  return `Filed ${entry.filed_on} · ${entry.filing_date_confidence}`;
};

const datePresetFromParams = (params) => {
  const storedPreset = params.get("filed_preset");
  if (
    storedPreset === "six_months" ||
    storedPreset === "one_year" ||
    storedPreset === "custom"
  ) {
    return storedPreset;
  }
  const after = params.get("filed_after") || "";
  const before = params.get("filed_before") || "";
  if (!after && !before) return "all";
  if (
    after === format(subMonths(new Date(), 6), "yyyy-MM-dd") &&
    before === TODAY()
  ) {
    return "six_months";
  }
  if (
    after === format(subYears(new Date(), 1), "yyyy-MM-dd") &&
    before === TODAY()
  ) {
    return "one_year";
  }
  return "custom";
};

const hiddenTagCount = (entry) =>
  Math.max(0, (entry.condition_issue_tags || []).length - 2) +
  Math.max(0, (entry.legal_issue_tags || []).length - 2) +
  Math.max(0, (entry.case_tags || []).length - 3);

export default function BriefBank() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryInput, setQueryInput] = useState(searchParams.get("q") || "");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({
    practice_areas: [],
    brief_types: [],
    condition_issue_tags: [],
    legal_issue_tags: [],
    case_tags: [],
  });
  const [loading, setLoading] = useState(true);
  const [facetLoading, setFacetLoading] = useState(true);
  const [facetError, setFacetError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copyEntry, setCopyEntry] = useState(null);
  const [previewEntry, setPreviewEntry] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const detailRequestRef = useRef(0);
  const [datePreset, setDatePreset] = useState(() =>
    datePresetFromParams(searchParams)
  );

  const page = Number(searchParams.get("page") || 1);
  const finalOnly = searchParams.get("final_only") !== "false";
  const practiceArea = searchParams.get("practice_area") || "";
  const briefTypes = selectedValues(searchParams, "brief_type");
  const selectedConditionIssueTags = selectedValues(
    searchParams,
    "condition_issue_tag"
  );
  const conditionIssueTags =
    selectedConditionIssueTags.length > 0
      ? selectedConditionIssueTags
      : selectedValues(searchParams, "issue_tag");
  const legalIssueTags = selectedValues(searchParams, "legal_issue_tag");
  const caseTags = selectedValues(searchParams, "case_tag");
  const filedAfter = searchParams.get("filed_after") || "";
  const filedBefore = searchParams.get("filed_before") || "";
  const sort = searchParams.get("sort") || "";
  const paramsKey = searchParams.toString();

  useEffect(() => {
    setQueryInput(searchParams.get("q") || "");
    setDatePreset(datePresetFromParams(searchParams));
    // Keep the visible query aligned with browser back/forward navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const updateParams = (mutate) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    next.set("page", "1");
    setSearchParams(next);
  };

  useEffect(() => {
    let cancelled = false;
    setFacetLoading(true);
    setFacetError("");
    fetchBriefBankFacets({ finalOnly })
      .then((response) => {
        if (!cancelled) setFacets(responseData(response));
      })
      .catch((requestError) => {
        if (!cancelled) {
          setFacetError(
            apiError(requestError, "Brief Bank filters could not load.")
          );
        }
      })
      .finally(() => {
        if (!cancelled) setFacetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [finalOnly, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSearchError("");
    fetchBriefBankEntries({
      page,
      query: searchParams.get("q") || "",
      practiceArea,
      briefTypes,
      conditionIssueTags,
      legalIssueTags,
      caseTags,
      filedAfter,
      filedBefore,
      finalOnly,
      sort,
    })
      .then((response) => {
        if (cancelled) return;
        const data = responseData(response);
        setEntries(data.results || []);
        setTotal(data.count || 0);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setEntries([]);
          setTotal(0);
          setSearchError(apiError(requestError, "Brief Bank search failed."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // paramsKey intentionally represents every URL-backed filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, reloadKey]);

  const openDetail = async (entry) => {
    const requestId = ++detailRequestRef.current;
    setPreviewEntry(null);
    setSelectedEntry(entry);
    setDetailLoading(true);
    try {
      const response = await fetchBriefBankEntry(entry.id);
      if (requestId === detailRequestRef.current) {
        const detail = responseData(response);
        setSelectedEntry(detail);
        setPreviewEntry(detail);
      }
    } catch (requestError) {
      if (requestId === detailRequestRef.current) {
        message.error(apiError(requestError, "Brief details could not load."));
      }
    } finally {
      if (requestId === detailRequestRef.current) {
        setDetailLoading(false);
      }
    }
  };

  const practiceOptions = useMemo(
    () => [
      { value: "", label: "All practice areas" },
      ...(facets.practice_areas || []).map((item) => ({
        value: item.practice_area,
        label: `${formatPracticeArea(item.practice_area)} (${item.count})`,
      })),
    ],
    [facets.practice_areas]
  );

  const applyDatePreset = (value) => {
    setDatePreset(value);
    if (value === "custom") {
      const next = new URLSearchParams(searchParams);
      next.set("filed_preset", "custom");
      setSearchParams(next);
      return;
    }
    updateParams((next) => {
      next.delete("filed_after");
      next.delete("filed_before");
      next.delete("filed_preset");
      if (value === "six_months") {
        next.set("filed_preset", "six_months");
        next.set("filed_after", format(subMonths(new Date(), 6), "yyyy-MM-dd"));
        next.set("filed_before", TODAY());
      }
      if (value === "one_year") {
        next.set("filed_preset", "one_year");
        next.set("filed_after", format(subYears(new Date(), 1), "yyyy-MM-dd"));
        next.set("filed_before", TODAY());
      }
    });
  };

  const resetFilters = () => {
    setQueryInput("");
    setDatePreset("all");
    setSearchParams(new URLSearchParams({ page: "1", final_only: "true" }));
  };

  return (
    <Page>
      <Header>
        <div>
          <Title>Brief Bank</Title>
          <Subtitle>
            Find proven firm work product and copy an independent Word template
            into a new Matter.
          </Subtitle>
        </div>
        <Button
          type="primary"
          icon={<UploadSimple size={17} />}
          onClick={() => setUploadOpen(true)}
        >
          Upload brief
        </Button>
      </Header>

      <SearchPanel>
        <SearchRow
          onSubmit={(event) => {
            event.preventDefault();
            updateParams((next) => {
              if (queryInput.trim()) next.set("q", queryInput.trim());
              else next.delete("q");
            });
          }}
        >
          <Input
            size="large"
            prefix={<MagnifyingGlass size={18} />}
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            placeholder='Search brief text, such as "Korea"'
            allowClear
          />
          <Button type="primary" size="large" htmlType="submit">
            Search
          </Button>
        </SearchRow>

        <Filters>
          <FilterLabel>
            Practice area
            <Select
              loading={facetLoading}
              value={practiceArea}
              options={practiceOptions}
              onChange={(value) =>
                updateParams((next) => {
                  if (value) next.set("practice_area", value);
                  else next.delete("practice_area");
                })
              }
            />
          </FilterLabel>
          <FilterLabel>
            Brief type
            <Select
              mode="multiple"
              loading={facetLoading}
              value={briefTypes}
              placeholder="All brief types"
              options={(facets.brief_types || []).map((item) => ({
                value: item.value,
                label: `${item.label} (${item.count})`,
              }))}
              onChange={(values) =>
                updateParams((next) => setMany(next, "brief_type", values))
              }
            />
          </FilterLabel>
          <FilterLabel>
            Condition / issue
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              loading={facetLoading}
              value={conditionIssueTags}
              placeholder="Any condition or issue"
              options={(facets.condition_issue_tags || []).map((item) => ({
                value: item.slug,
                label: `${item.label} (${item.count})`,
              }))}
              onChange={(values) =>
                updateParams((next) => {
                  next.delete("issue_tag");
                  setMany(next, "condition_issue_tag", values)
                })
              }
            />
          </FilterLabel>
          <FilterLabel>
            Specific legal issue
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              loading={facetLoading}
              value={legalIssueTags}
              placeholder="Any specific legal issue"
              options={(facets.legal_issue_tags || []).map((item) => ({
                value: item.slug,
                label: `${item.label} (${item.count})`,
              }))}
              onChange={(values) =>
                updateParams((next) =>
                  setMany(next, "legal_issue_tag", values)
                )
              }
            />
          </FilterLabel>
          <FilterLabel>
            Cases cited
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              loading={facetLoading}
              value={caseTags}
              placeholder="Any cited case"
              options={(facets.case_tags || []).map((item) => ({
                value: item.slug,
                label: `${item.label} (${item.count})`,
              }))}
              onChange={(values) =>
                updateParams((next) => setMany(next, "case_tag", values))
              }
            />
          </FilterLabel>
        </Filters>

        <FilterFooter>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Select
              aria-label="Sort order"
              value={sort || "default"}
              style={{ width: 235 }}
              options={[
                { value: "default", label: "Default order" },
                {
                  value: "docket_desc",
                  label: "Docket number — descending",
                },
                {
                  value: "docket_asc",
                  label: "Docket number — ascending",
                },
              ]}
              onChange={(value) =>
                updateParams((next) => {
                  if (value === "default") next.delete("sort");
                  else next.set("sort", value);
                })
              }
            />
            <Select
              aria-label="Filed date"
              value={datePreset}
              style={{ width: 170 }}
              options={[
                { value: "all", label: "Any filed date" },
                { value: "six_months", label: "Last 6 months" },
                { value: "one_year", label: "Last year" },
                { value: "custom", label: "Custom range" },
              ]}
              onChange={applyDatePreset}
            />
            {datePreset === "custom" && (
              <RangePicker
                value={[
                  filedAfter ? dayjs(filedAfter) : null,
                  filedBefore ? dayjs(filedBefore) : null,
                ]}
                onChange={(values) =>
                  updateParams((next) => {
                    next.set("filed_preset", "custom");
                    if (values?.[0])
                      next.set("filed_after", values[0].format("YYYY-MM-DD"));
                    else next.delete("filed_after");
                    if (values?.[1])
                      next.set("filed_before", values[1].format("YYYY-MM-DD"));
                    else next.delete("filed_before");
                  })
                }
              />
            )}
            <Checkbox
              checked={finalOnly}
              onChange={(event) =>
                updateParams((next) =>
                  next.set("final_only", event.target.checked ? "true" : "false")
                )
              }
            >
              Final only
            </Checkbox>
          </div>
          <Button type="link" onClick={resetFilters}>
            Clear filters
          </Button>
        </FilterFooter>
      </SearchPanel>

      {(facetError || searchError) && (
        <div role="alert" style={{ marginTop: 14, color: "#b42318" }}>
          {[facetError, searchError].filter(Boolean).join(" ")}
        </div>
      )}

      <ResultSummary>
        {loading ? "Searching…" : `${total.toLocaleString()} briefs found`}
      </ResultSummary>

      {loading ? (
        <EmptyState style={{ textAlign: "center" }}>
          <Spin />
        </EmptyState>
      ) : entries.length === 0 ? (
        <EmptyState>
          <Empty description="No briefs match these filters." />
        </EmptyState>
      ) : (
        <ResultList>
          {entries.map((entry) => (
              <ResultCard
                key={entry.id}
                role="button"
                tabIndex={0}
                onClick={() => openDetail(entry)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openDetail(entry);
                  }
                }}
              >
                <ResultTop>
                  <div>
                    <ResultTitle>{entry.title}</ResultTitle>
                    <Meta>
                      <span>{entry.brief_type_label}</span>
                      <span>{formatPracticeArea(entry.practice_area)}</span>
                      <span>{entry.source_matter_label}</span>
                      <span>{dateDescription(entry)}</span>
                    </Meta>
                  </div>
                  <Tag color={entry.is_final ? "green" : "default"}>
                    {entry.is_final ? "Final" : "Draft"}
                  </Tag>
                </ResultTop>
                <TagRow>
                  {(entry.condition_issue_tags || []).slice(0, 2).map((tag) => (
                    <Tag color="blue" key={tag.id}>
                      {tag.label}
                    </Tag>
                  ))}
                  {(entry.legal_issue_tags || []).slice(0, 2).map((tag) => (
                    <Tag color="purple" key={tag.id}>
                      {tag.label}
                    </Tag>
                  ))}
                  {(entry.case_tags || []).slice(0, 3).map((tag) => (
                    <Tag key={tag.id}>{tag.label}</Tag>
                  ))}
                  {hiddenTagCount(entry) > 0 && (
                    <Tag>+{hiddenTagCount(entry)}</Tag>
                  )}
                </TagRow>
                {entry.snippet && (
                  <Snippet>{highlightedSnippet(entry.snippet)}</Snippet>
                )}
              </ResultCard>
          ))}
        </ResultList>
      )}
      {!loading && total > 0 && (
        <PaginationRow>
          <Pagination
            current={page}
            pageSize={25}
            total={total}
            showSizeChanger={false}
            onChange={(nextPage) => {
              const next = new URLSearchParams(searchParams);
              next.set("page", String(nextPage));
              setSearchParams(next);
            }}
          />
        </PaginationRow>
      )}

      <Drawer
        title={selectedEntry?.title || "Brief details"}
        width={previewEntry ? "min(1500px, calc(100vw - 24px))" : 520}
        open={Boolean(selectedEntry)}
        onClose={() => {
          detailRequestRef.current += 1;
          setPreviewEntry(null);
          setSelectedEntry(null);
          setDetailLoading(false);
        }}
      >
        {detailLoading ? (
          <Spin />
        ) : (
          selectedEntry && (
            <DetailLayout $withPreview={Boolean(previewEntry)}>
              <DetailInfo>
                <DetailSection>
                <DetailHeading>Source</DetailHeading>
                <DetailValue>
                  {selectedEntry.source_matter_label || "Unknown Matter"}
                  <br />
                  {selectedEntry.source_name}
                  {selectedEntry.source_relative_path && (
                    <>
                      <br />
                      <a
                        href={`/matter${selectedEntry.source_relative_path}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open source Matter
                      </a>
                    </>
                  )}
                </DetailValue>
              </DetailSection>
              <DetailSection>
                <DetailHeading>Classification</DetailHeading>
                <DetailValue>
                  {selectedEntry.brief_type_label}
                  <br />
                  {dateDescription(selectedEntry)}
                  <br />
                  {selectedEntry.is_final ? "Final Word version" : "No final PDF match"}
                </DetailValue>
              </DetailSection>
              <DetailSection>
                <DetailHeading>Conditions / issues</DetailHeading>
                <TagRow>
                  {(selectedEntry.condition_issue_tags || []).map((tag) => (
                    <Tag color="blue" key={tag.id}>
                      {tag.label}
                    </Tag>
                  ))}
                  {(selectedEntry.condition_issue_tags || []).length === 0 &&
                    "No condition / issue tags"}
                </TagRow>
              </DetailSection>
              <DetailSection>
                <DetailHeading>Specific legal issues</DetailHeading>
                <TagRow>
                  {(selectedEntry.legal_issue_tags || []).map((tag) => (
                    <Tag color="purple" key={tag.id}>
                      {tag.label}
                    </Tag>
                  ))}
                  {(selectedEntry.legal_issue_tags || []).length === 0 &&
                    "No specific legal issue tags"}
                </TagRow>
              </DetailSection>
              <DetailSection>
                <DetailHeading>Cases cited</DetailHeading>
                <TagRow>
                  {(selectedEntry.case_tags || []).map((tag) => (
                    <Tag key={tag.id} title={tag.canonical_citation}>
                      {tag.label}
                    </Tag>
                  ))}
                  {(selectedEntry.case_tags || []).length === 0 && "No cited cases"}
                </TagRow>
                </DetailSection>
                <DrawerFooter>
                  <Button
                    type="primary"
                    onClick={() => setCopyEntry(selectedEntry)}
                  >
                    Use as template
                  </Button>
                </DrawerFooter>
              </DetailInfo>
              {previewEntry && (
                <InlinePreviewPanel>
                  <BriefBankPreviewModal
                    embedded
                    open
                    entry={previewEntry}
                    onClose={() => setPreviewEntry(null)}
                  />
                </InlinePreviewPanel>
              )}
            </DetailLayout>
          )
        )}
      </Drawer>

      {copyEntry && (
        <BriefBankCopyModal
          open
          entry={copyEntry}
          onClose={() => setCopyEntry(null)}
          onCopied={(created) => {
            if (
              typeof created?.relative_path !== "string" ||
              !created.relative_path.startsWith("/")
            ) {
              message.error(
                "The brief was copied, but DMS did not return a valid document path."
              );
              setCopyEntry(null);
              return;
            }
            message.success("Brief copied. Opening the new Word document…");
            setCopyEntry(null);
            globalThis.location.href = `/matter${created.relative_path}`;
          }}
        />
      )}
      {uploadOpen && (
        <BriefBankUploadModal
          open
          facets={facets}
          onClose={() => setUploadOpen(false)}
          onUploaded={(entry) => {
            message.success("Brief uploaded and added to the bank.");
            setUploadOpen(false);
            setSelectedEntry(entry);
            setPreviewEntry(entry);
            setReloadKey((value) => value + 1);
          }}
        />
      )}
    </Page>
  );
}
